/**
 * Viewer — 3D Configurator Screen
 *
 * The main experience screen. The engine canvas fills the entire
 * viewport (h-dvh). All controls use glass morphism (backdrop-blur).
 *
 * Layout layers (bottom → top):
 *   1. Engine canvas (position absolute, full viewport)
 *   2. Top bar: Return (left) | CameraSwitch (center) | AR (right)
 *   3. SubMenuPanel (above MenuBar, slides in from bottom)
 *   4. MenuBar (pill at bottom, feature-flag driven)
 *   5. Error Overlay (via App)
 *
 * Single control system — all scene/color/rim/animation controls flow
 * through MenuBar → activeSubMenu → SubMenuPanel → <XxxSubMenu>.
 * No floating independent controls (former SceneSwitcher removed).
 *
 * Engine cleanup on return:
 *   returnToStart() sets screen → 'start'. App.tsx conditionally mounts
 *   Viewer only for screen === 'loading' | 'viewer'. When Viewer unmounts,
 *   useEngine's cleanup fires engine.dispose() — freeing GPU, GLB, textures,
 *   animations. No manual cleanup needed here.
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md#screen-3--viewer--configurator
 * @see packages/brand-audi/src/hooks/useEngine.ts
 */
import { useRef, useCallback, useEffect } from 'react';
import { useAppStore, PANEL_SUBMENUS } from '../../store/app.store';
import { useEngine } from '../../hooks/useEngine';
import { useColor } from '../../hooks/useColor';
import { useRim } from '../../hooks/useRim';
import { useInteriorVariant } from '../../hooks/useInteriorVariant';
import { useWheelSteering } from '../../hooks/useWheelSteering';
import { useEmbedApi } from '../../hooks/useEmbedApi';
import { GlassIconButton } from '../ui/GlassIconButton';
import { CameraSwitchPill } from '../ui/CameraSwitchPill';
import { PanoBar } from '../ui/PanoBar';
import { EdgeViewCycler } from '../ui/EdgeViewCycler';
import { MenuBar } from '../ui/MenuBar';
import { SubMenuPanel } from '../ui/SubMenuPanel';
import { RimLoadingHint } from '../ui/RimLoadingHint';
import { tracker, EVENTS } from '../../services/tracking';
import { SceneSubMenu } from '../ui/submenus/SceneSubMenu';
import { ColorSubMenu } from '../ui/submenus/ColorSubMenu';
import { RimSubMenu } from '../ui/submenus/RimSubMenu';
import type { CameraViewMode } from '../ui/CameraSwitchPill';
import type { ResolvedConfig } from '@ims/shared';
import { isMobilePhone } from '../../utils/isMobilePhone';

interface ViewerProps {
  config: ResolvedConfig | null;
  onArOpen?: () => void;
}

// Icon paths (public/app/icons/)
const ICON_RETURN = '/app/icons/audi-icon-switchModel.svg';
const ICON_AR = '/app/icons/icon-ar.svg';

export function Viewer({ config, onArOpen }: ViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const engineRef = useEngine(containerRef, config);
  // Reactive color changes: store.selectedColorId → engine.setMaterial()
  useColor(engineRef, config);
  // Reactive rim swaps: store.selectedRimId → engine.swapAsset()
  useRim(engineRef, config);
  // Reactive interior variant swaps: store.selectedPanoId → engine.setMaterial({ textures })
  useInteriorVariant(engineRef, config);
  // Auto-steer front wheels when studio scene is active (rs5limo only — no-op when config lacks wheelSteering)
  useWheelSteering(engineRef, config);

  const activeSubMenu = useAppStore((s) => s.activeSubMenu);
  const sceneReady = useAppStore((s) => s.sceneReady);

  // Embed postMessage API — bidirectional control for iframe embeds
  useEmbedApi({ engineRef, config, sceneReady });

  const cameraMode = useAppStore((s) => s.cameraMode);
  const setCameraMode = useAppStore((s) => s.setCameraMode);
  const returnToStart = useAppStore((s) => s.returnToStart);
  const arActive = useAppStore((s) => s.arActive);
  const selectedPanoId = useAppStore((s) => s.selectedPanoId);
  const embedMode = useAppStore((s) => s.embedMode);
  const kioskMode = useAppStore((s) => s.kioskMode);
  const embedControls = useAppStore((s) => s.embedControls);
  const transparentBg = useAppStore((s) => s.transparentBg);

  // Derived: what UI elements to show in embed mode
  const showTopBar = !embedMode || embedControls === 'full';
  const showMenuBar = !embedMode || embedControls === 'full';
  const showColorOnly = embedMode && embedControls === 'minimal';

  // ── Pause engine render-loop while AR is active ─────────────────────
  // Frees GPU so WebXR / Quick Look run smoothly.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.setViewerSettings({ paused: arActive });
  }, [arActive, engineRef]);

  // True when the active submenu is a panel type (not an overlay).
  // Passed to SubMenuPanel to trigger the slide-up animation.
  const isPanelSubMenu = PANEL_SUBMENUS.has(activeSubMenu);

  // Whether to show the Exterior / Interior switch pill
  const showCameraSwitch =
    config?.features.interiorPano === true &&
    (Boolean(config?.model.assets.panoUrl) || (config?.panoVariants?.length ?? 0) > 0);

  // Whether this model has multiple pano variants (show PanoBar)
  const hasPanoVariants = (config?.panoVariants?.length ?? 0) > 1;

  const exteriorLabel = config?.ui.cameraSwitchExterior ?? 'Exterior';
  const interiorLabel = config?.ui.cameraSwitchInterior ?? 'Interior';

  const handleCameraSwitch = useCallback(
    (newMode: CameraViewMode) => {
      if (newMode === cameraMode) return;
      setCameraMode(newMode);
      tracker.track(EVENTS.VIEWPOINT_CHANGED, { viewpoint: newMode });

      const engine = engineRef.current;
      if (!engine) return;

      if (newMode === 'interior') {
        // Prefer the last-selected pano variant; fall back to legacy single-URL, then first variant.
        const selectedVariant = selectedPanoId
          ? config?.panoVariants?.find((v) => v.id === selectedPanoId)
          : undefined;
        const mobile = isMobilePhone();
        const pickUrl = (mobileUrl: string | undefined, url: string | undefined) =>
          (mobile && mobileUrl) || url;
        const panoUrl =
          pickUrl(selectedVariant?.mobileUrl, selectedVariant?.url) ??
          pickUrl(config?.model.assets.mobilePanoUrl, config?.model.assets.panoUrl) ??
          pickUrl(config?.panoVariants?.[0]?.mobileUrl, config?.panoVariants?.[0]?.url);
        if (panoUrl) engine.showPano(panoUrl);
      } else {
        engine.hidePano();
      }
    },
    [cameraMode, setCameraMode, engineRef, config, selectedPanoId],
  );

  return (
    <div className={`h-full w-full relative overflow-hidden${transparentBg ? '' : ' bg-black'}`}>
      {/* ── Engine canvas — engine mounts its own R3F canvas here ──────── */}
      <div
        ref={containerRef}
        className="absolute inset-0 w-full h-full"
        aria-label="3D vehicle configurator"
        style={arActive ? { visibility: 'hidden' } : undefined}
      />

      {/* ── Vignette overlay — pure CSS, 0 GPU shader cost ──────────────── *
       *  Radial gradient from transparent center to soft black at edges.    *
       *  Adds depth and frames the car elegantly without darkening center.  *
       *  pointer-events-none so all touch/mouse events pass through.        *
       *  Hidden in transparent mode — would break the see-through effect.   *
       * ──────────────────────────────────────────────────────────────────── */}
      {!transparentBg && (
        <div
          className="absolute inset-0 pointer-events-none z-10"
          style={{
            background:
              'radial-gradient(ellipse at 50% 55%, transparent 45%, rgba(0,0,0,0.10) 58%, rgba(0,0,0,0.28) 74%, rgba(0,0,0,0.52) 90%, rgba(0,0,0,0.65) 100%)',
          }}
          aria-hidden="true"
        />
      )}

      {/* ── Edge tap zones — hidden view cycling ────────────────────────── */}
      <EdgeViewCycler
        engineRef={engineRef}
        namedViews={config?.model.camera.namedViews}
        enabled={sceneReady && cameraMode === 'exterior'}
      />

      {/* ── Top bar ─────────────────────────────────────────────────────── *
       *  Fades in after sceneReady — prevents controls from showing over    *
       *  the loading progress. Absolute, sits above the canvas (z-20).      *
       *                                                                      *
       *  Layout: Return (left) | CameraSwitch (center, absolute) | AR (right)*
       *  The pill is positioned absolutely so it stays centered regardless  *
       *  of button widths on either side.                                   *
       * ─────────────────────────────────────────────────────────────────── */}
      {showTopBar && (
        <div
          className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-5 pt-[17.5px] pointer-events-none"
          style={{
            opacity: sceneReady ? 1 : 0,
            transition: 'opacity 1000ms ease',
          }}
        >
          {/* Return — top-left (hidden in embed/kiosk mode — no carousel to return to) */}
          {!embedMode && !kioskMode && (
            <GlassIconButton
              icon={ICON_RETURN}
              label="Back to model selection"
              onClick={returnToStart}
              className="pointer-events-auto"
            />
          )}
          {/* Spacer when return button is hidden in embed/kiosk mode */}
          {(embedMode || kioskMode) && <div />}

          {/* CameraSwitch — center (absolute so it doesn't affect flex spacing) */}
          {showCameraSwitch && (
            <CameraSwitchPill
              mode={cameraMode}
              onChange={handleCameraSwitch}
              exteriorLabel={exteriorLabel}
              interiorLabel={interiorLabel}
              className="absolute left-1/2 -translate-x-1/2 pointer-events-auto"
            />
          )}

          {/* AR — top-right */}
          <GlassIconButton
            icon={ICON_AR}
            label="Start augmented reality"
            ripple
            className="pointer-events-auto"
            onClick={() => {
              onArOpen?.();
              tracker.track(EVENTS.AR_MODE);
            }}
          />
        </div>
      )}

      {/* ── SubMenuPanel — slides up above the MenuBar pill ───────────────
       *  Hosts all panel-type submenus (color, scene, rim, animation).
       *  Overlay-type submenus (info, ar, cookie) render separately.
       * ────────────────────────────────────────────────────────────────── */}
      {showMenuBar && (
        <SubMenuPanel isOpen={isPanelSubMenu} submenuKey={activeSubMenu}>
          {activeSubMenu === 'scene' && <SceneSubMenu config={config} engineRef={engineRef} />}
          {activeSubMenu === 'color' && config && <ColorSubMenu colors={config.colors} />}
          {activeSubMenu === 'rim' && config && config.rims.length > 0 && (
            <RimSubMenu rims={config.rims} defaultRimId={config.model.rims?.defaultId ?? ''} />
          )}
        </SubMenuPanel>
      )}

      {/* ── Rim-loading hint — floats above the scaled MenuBar pill ────── */}
      {showMenuBar && (
        <RimLoadingHint
          visible={isPanelSubMenu && activeSubMenu === 'rim'}
          label={config?.ui.rimLoading}
        />
      )}

      {/* ── MenuBar — centered pill at bottom, feature-flag driven ───────── */}
      {showMenuBar && (
        <MenuBar
          config={config}
          sceneReady={sceneReady}
          engineRef={engineRef}
          hidden={cameraMode === 'interior'}
        />
      )}

      {/* ── Minimal embed color bar — only color swatches at bottom ──────── */}
      {showColorOnly && config && config.colors.length > 0 && (
        <div
          className="absolute bottom-0 inset-x-0 z-20 pb-4"
          style={{
            opacity: sceneReady ? 1 : 0,
            transition: 'opacity 1000ms ease',
          }}
        >
          <ColorSubMenu colors={config.colors} />
        </div>
      )}

      {/* ── PanoBar — replaces MenuBar in interior mode (bottom pano tiles) ── */}
      {showMenuBar && hasPanoVariants && (
        <PanoBar config={config} engineRef={engineRef} visible={cameraMode === 'interior'} />
      )}
    </div>
  );
}
